import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Block, Close, Delete, Edit, InfoOutlined, RestoreFromTrash } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, forms, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { formatValidity, healthPlansService, HealthPlanOperatorOption, HealthPlanRow } from "../services/healthPlansService";

/**
 * Serviços e convênios → Planos (RN-411b): os planos (categorias) de cada convênio, com vigência
 * opcional. O plano não troca de convênio na edição; plano com paciente vinculado não se exclui —
 * inativa. Fora da vigência, sai da escolha no cadastro do paciente.
 */

interface FormState {
  id: string | null;
  operatorId: string;
  name: string;
  ansProductCode: string;
  copayment: "" | "yes" | "no";
  startDate: string;
  endDate: string;
  active: boolean;
}

// Rodapé de modal do design system: separado por borda, fundo claro
const dialogActionsSx = {
  padding: "1.5rem 2rem",
  borderTop: `1px solid ${colors.backgroundAlt}`,
  backgroundColor: colors.background,
  gap: "1rem",
};

const digits = (v: string) => v.replace(/\D/g, "");

const copaymentLabel = (v: boolean | null) => (v === null ? "—" : v ? "Sim" : "Não");

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const HealthPlans: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<HealthPlanRow[]>([]);
  const [operators, setOperators] = useState<HealthPlanOperatorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HealthPlanRow | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await healthPlansService.getAll();
      setRows(data.plans);
      setOperators(data.operators);
    } catch {
      showToast("Erro ao carregar os planos.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Planos";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows
      .filter((r) => operatorFilter === "all" || r.operatorId === operatorFilter)
      .filter((r) => statusFilter === "all" || (statusFilter === "active" ? r.active : !r.active))
      .filter((r) => !term || normalize(`${r.operatorName} ${r.name} ${r.ansProductCode ?? ""}`).includes(term));
  }, [rows, search, operatorFilter, statusFilter]);

  const hasFilterChanges = search !== "" || operatorFilter !== "all" || statusFilter !== "active";
  const clearFilters = () => { setSearch(""); setOperatorFilter("all"); setStatusFilter("active"); setCurrentPage(1); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filtered, currentPage, itemsPerPage],
  );

  // O filtro pode deixar o usuário numa página que não existe mais
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const activeOperators = operators.filter((o) => o.active);

  const openCreate = () => {
    if (activeOperators.length === 0) {
      showToast("Cadastre um convênio ativo antes de incluir planos.", "error");
      return;
    }
    setForm({
      id: null,
      operatorId: operatorFilter !== "all" && activeOperators.some((o) => o.id === operatorFilter) ? operatorFilter : "",
      name: "", ansProductCode: "", copayment: "", startDate: "", endDate: "", active: true,
    });
  };

  const openEdit = (row: HealthPlanRow) => setForm({
    id: row.id,
    operatorId: row.operatorId,
    name: row.name,
    ansProductCode: row.ansProductCode ?? "",
    copayment: row.copayment === null ? "" : row.copayment ? "yes" : "no",
    startDate: row.startDate ?? "",
    endDate: row.endDate ?? "",
    active: row.active,
  });

  const save = async () => {
    if (!form) return;
    if (!form.operatorId) { showToast("Escolha o convênio.", "error"); return; }
    if (form.startDate && form.endDate && form.endDate < form.startDate) { showToast("O fim da vigência não pode ser anterior ao início.", "error"); return; }
    if (!form.name.trim()) { showToast("Informe o nome do plano.", "error"); return; }
    if (form.ansProductCode && form.ansProductCode.length !== 9) { showToast("O registro do plano na ANS tem 9 dígitos.", "error"); return; }
    setSaving(true);
    try {
      await healthPlansService.save({
        id: form.id,
        operatorId: form.operatorId,
        name: form.name.trim(),
        ansProductCode: form.ansProductCode || null,
        copayment: form.copayment === "" ? null : form.copayment === "yes",
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        active: form.active,
      });
      showToast(form.id ? "Plano atualizado." : "Plano incluído.", "success");
      setForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar o plano.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: HealthPlanRow) => {
    try {
      await healthPlansService.save({
        id: row.id, operatorId: row.operatorId, name: row.name, ansProductCode: row.ansProductCode,
        copayment: row.copayment, startDate: row.startDate, endDate: row.endDate, active: !row.active,
      });
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar o plano.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await healthPlansService.remove(deleteTarget.id);
      showToast("Plano excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir o plano.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Na edição o convênio é fixo (RN-411b); inativo continua listado para aparecer no campo
  const formOperators = form?.id ? operators.filter((o) => o.id === form.operatorId) : activeOperators;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Planos</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Os planos de cada convênio, oferecidos no vínculo do paciente.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder="Convênio, plano ou registro ANS"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  select
                  size="small"
                  label="Convênio"
                  value={operatorFilter}
                  onChange={(e) => { setOperatorFilter(e.target.value); setCurrentPage(1); }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {operators.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.active ? o.name : `${o.name} (inativo)`}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setCurrentPage(1); }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={openCreate} title="Novo plano" />
              </div>
            </div>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && (
              <>
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    Todo plano pertence a um convênio cadastrado em Convênios. Fora da vigência, some da escolha no cadastro do paciente. Com paciente vinculado, não se exclui, só inativa.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <ResultCount shown={filtered.length} total={rows.length} />

                  {filtered.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {operators.length === 0
                        ? "Nenhum convênio cadastrado — cadastre o convênio antes de incluir planos."
                        : rows.length === 0
                          ? "Nenhum plano cadastrado — inclua o primeiro no botão acima."
                          : "Nenhum plano encontrado com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 240px", minWidth: 200 }}>Plano</Box>
                        <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "1 1 160px", minWidth: 140 }}>Convênio</Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 130px" }}>Registro ANS</Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 130px" }}>Coparticipação</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 170px" }}>Vigência</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 90px" }}>Pacientes</Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px" }}>Situação</Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}>Ações</Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {paged.map((row) => (
                          <Box key={row.id} className="c4u-table__row">
                            <Box className="admin-plans-cell admin-plans-name" sx={{ flex: "2 1 240px", minWidth: 200 }} data-label="Plano">
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.name}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--sm" sx={{ flex: "1 1 160px", minWidth: 140 }} data-label="Convênio">
                              <Typography sx={{ fontSize: 13 }}>{row.operatorActive ? row.operatorName : `${row.operatorName} (inativo)`}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 130px" }} data-label="Registro ANS">
                              <Typography sx={{ fontSize: 13 }}>{row.ansProductCode || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 130px" }} data-label="Coparticipação">
                              <Typography sx={{ fontSize: 13 }}>{copaymentLabel(row.copayment)}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 170px" }} data-label="Vigência">
                              <Typography sx={{ fontSize: 13 }}>{formatValidity(row.startDate, row.endDate)}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 90px" }} data-label="Pacientes">
                              <Typography sx={{ fontSize: 13 }}>{row.patientsCount}</Typography>
                            </Box>
                            <Box className="admin-plans-cell" sx={{ flex: "0 0 150px" }} data-label="Situação">
                              {/* Status DERIVADO (RN-411f): plano ativo cujo convênio está
                                  desligado não vale hoje — e o selo diz o porquê, em vez de
                                  deixar a pessoa procurar. Nada é gravado no plano. */}
                              <Chip
                                label={row.blockedByOperator ? "Inativo pela operadora" : row.active ? "Ativo" : "Inativo"}
                                size="small"
                                title={row.blockedByOperator
                                  ? "O plano está ativo, mas o convênio está inativo ou fora da vigência: ele não aparece para escolher enquanto isso."
                                  : undefined}
                                sx={{
                                  color: "white",
                                  fontWeight: 600,
                                  backgroundColor: row.blockedByOperator ? "#fd7e14" : row.active ? colors.success : "#adb5bd",
                                }}
                              />
                            </Box>
                            <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 130px" }} data-label="Ações">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar plano">
                                  <IconButton className="c4u-row-action" size="small" onClick={() => openEdit(row)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={row.active ? "Inativar (sai do vínculo; o histórico dos pacientes fica)" : "Reativar plano"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() => toggleActive(row)}
                                    sx={rowActionSx(row.active ? "#fd7e14" : colors.success, row.active ? "#e8590c" : "#218838")}
                                  >
                                    {row.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                                  </IconButton>
                                </Tooltip>
                                {row.patientsCount === 0 && (
                                  <Tooltip title="Excluir (plano sem pacientes)">
                                    <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteTarget(row)} sx={rowActionSx("#dc3545", "#c82333")}>
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {filtered.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filtered.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                    />
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar plano" : "Novo plano"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: forms.fieldGapX, rowGap: forms.fieldGapY, alignItems: "center" }}>
              <TextField
                select
                label="Convênio"
                value={form.operatorId}
                onChange={(e) => setForm({ ...form, operatorId: e.target.value })}
                size="small"
                fullWidth
                required
                disabled={form.id !== null}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              >
                <MenuItem value="" disabled>Selecione</MenuItem>
                {formOperators.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </TextField>
              <TextField
                label="Nome do plano"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                size="small"
                fullWidth
                required
                placeholder="Ex.: Nacional Apartamento"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                label="Registro do plano na ANS"
                value={form.ansProductCode}
                onChange={(e) => setForm({ ...form, ansProductCode: digits(e.target.value).slice(0, 9) })}
                size="small"
                fullWidth
                placeholder="9 dígitos (opcional)"
                inputProps={{ maxLength: 9, inputMode: "numeric" }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Coparticipação"
                value={form.copayment}
                onChange={(e) => setForm({ ...form, copayment: e.target.value as FormState["copayment"] })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Não informado</MenuItem>
                <MenuItem value="yes">Sim</MenuItem>
                <MenuItem value="no">Não</MenuItem>
              </TextField>
              <TextField
                label="Vigência de"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Vigência até"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                size="small"
                fullWidth
                error={Boolean(form.startDate && form.endDate && form.endDate < form.startDate)}
                helperText={form.startDate && form.endDate && form.endDate < form.startDate ? "Anterior ao início" : "Vazio = sem data de fim"}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label={form.active ? "Ativo" : "Inativo"}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setForm(null)} variant="outlined" disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            onClick={save}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir plano"
        message={`Excluir "${deleteTarget?.operatorName ?? ""} · ${deleteTarget?.name ?? ""}"? Só é possível porque nenhum paciente usa este plano.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default HealthPlans;
